import { useState } from 'react'
import { timesHash } from '../components/shared/utils'
import useCurrentUser from '../components/helpers/useCurrentUser'
import { format } from 'date-fns'
import { ResourcesAdjustmentShape, ResourcesRecordShape } from '../api/api_data_types'

function isResourcesRecord(record: ResourcesRecordShape | ResourcesAdjustmentShape): record is ResourcesRecordShape {
  return record.type !== 'Resources::Adjustment'
}

export type ResourcesRecordFormController = {
  formData: any
  setFormData: (data: any) => void
  transform: (data: any) => any
  successfullyCreated: boolean
  setSuccessfullyCreated: (successfullyCreated: boolean) => void
}

export default function useResourcesRecordFormController(record: ResourcesRecordShape | ResourcesAdjustmentShape): ResourcesRecordFormController {
  const currentUser = useCurrentUser()
  const [successfullyCreated, setSuccessfullyCreated] = useState(false)
  const jobnumber = record.jobnumber
  const jobnumberId = (jobnumber && jobnumber.id) || 0

  const initialDate = record.date || format(new Date(), 'yyyy-MM-dd')
  const initialMachines = record.machines && record.machines.length > 0 ? record.machines : []
  const initialItems = record.items && record.items.length > 0 ? record.items : []
  const initialPackagings = record.packagings && record.packagings.length > 0 ? record.packagings : []

  let initialShipments = []
  let initialPurchases = []
  if (isResourcesRecord(record)) {
    initialShipments = record.shipments && record.shipments.length > 0 ? record.shipments : []
    initialPurchases = record.purchases && record.purchases.length > 0 ? record.purchases : []
  }

  let initialTimes = []
  if (record.times && record.times.length > 0) {
    initialTimes = record.times
  } else if (initialMachines.length === 0 && initialItems.length === 0 && initialPackagings.length === 0) {
    if (isResourcesRecord(record)) { // don't need default time record for adjustments
      const defaultTimeRecord = { worktype_id: currentUser.default_worktype_id || '' }
      initialTimes = [defaultTimeRecord]
    }
  }

  let initialPartnerRecords = []
  if (isResourcesRecord(record) && record.partner_records && record.partner_records.length > 0) {
    initialPartnerRecords = record.partner_records.map(partnerRecord => {
      return {
        id: partnerRecord.id,
        is_master: partnerRecord.is_master,
        partner_id: partnerRecord.user_id,
        times_attributes: partnerRecord.times ? timesHash(partnerRecord.times) : []
      }
    })
  }

  const [formData, setFormData] = useState({
    jobnumber_id: jobnumberId,
    task_id: record.task_id,
    date: initialDate,
    times_attributes: timesHash(initialTimes),
    partner_records_attributes: timesHash(initialPartnerRecords),
    machines_attributes: timesHash(initialMachines),
    items_attributes: timesHash(initialItems),
    packagings_attributes: timesHash(initialPackagings),
    shipments_attributes: timesHash(initialShipments),
    purchases_attributes: timesHash(initialPurchases),
    partner_ids: isResourcesRecord(record) ? record.partner_ids || [] : [],
    workcard_ids: isResourcesRecord(record) ? record.workcard_ids || [] : [],
  })

  const normalizeKeys = (obj: any) => {
    const result = {}
    Object.values(obj).forEach((v, k) => {
      result[k.toString()] = v
    })
    return result
  }

  const normalizePartnerRecordKeys = (attributes: any) => {
    const result = {}
    Object.values(attributes).forEach((v: {times_attributes: any}, k) => {
      const partnerRecord = { ...v }
      partnerRecord.times_attributes = normalizeKeys(partnerRecord.times_attributes)
      result[k.toString()] = partnerRecord
    })
    return result
  }

  const transform = data => {
    const transformedData = { ...data }
    transformedData.times_attributes = normalizeKeys(transformedData.times_attributes)
    transformedData.machines_attributes = normalizeKeys(transformedData.machines_attributes)
    transformedData.items_attributes = normalizeKeys(transformedData.items_attributes)
    transformedData.packagings_attributes = normalizeKeys(transformedData.packagings_attributes)
    transformedData.shipments_attributes = normalizeKeys(transformedData.shipments_attributes)
    transformedData.purchases_attributes = normalizeKeys(transformedData.purchases_attributes)
    transformedData.partner_records_attributes = normalizePartnerRecordKeys(transformedData.partner_records_attributes)
    return transformedData
  }


  return { formData, setFormData, transform, successfullyCreated, setSuccessfullyCreated }
}
